import { apiClient } from "../../client";
import { PaginatedResponse } from "../role.repository";
import { Chain } from "./chain.repository";
import { Channel } from "./channel.repository";
import { SubChannel } from "./sub-channel.repository";
import { StoreFormat } from "./store-format.repository";
import { TradeType } from "./trade-type.repository";
import { StoreClass } from "./store-class.repository";
import { Country } from "../universe/country.repository";
import { Region } from "../universe/region.repository";
import { City } from "../universe/city.repository";
import { Area } from "../universe/area.repository";
import { District } from "../universe/district.repository";


type QueryValue = string | number | boolean;

export interface Store {
    id: number;
    tenant_id: number;
    country_id: number;
    region_id: number;
    province_id: number;
    city_id: number;
    district_id: number;
    area_id: number;
    channel_id: number;
    sub_channel_id: number;
    chain_id: number;
    store_format_id: number;
    trade_type_id: number;
    store_class_id: number;
    en_name: string;
    ar_name: string;
    store_code: string;
    ref_id_1: string;
    ref_id_2: string;
    g_code: string;
    address: string;
    status: number;
    manager_name: string;
    manager_contact: string;
    manager_email: string;
    opening_hours: string;
    created_by?: number | null
    updated_by?: number | null
    created_at: Date
    updated_at: Date

    chain: Chain;
    channel: Channel;
    subChannel: SubChannel;
    storeFormat: StoreFormat;
    tradeType: TradeType;
    storeClass: StoreClass;
    country: Country;
    region: Region;
    city: City;
    area: Area;
    district: District;
    meta?: any;
    geo_fence?: number;
    is_photo_allowed?: number;
}

export interface CreateStore {
    tenant_id: number;

    en_name: string;
    ar_name?: string;
    status?: number;

    country_id?: number;
    region_id?: number;
    province_id?: number;
    city_id?: number;
    district_id?: number;
    area_id?: number;
    channel_id?: number;
    sub_channel_id?: number;
    chain_id?: number;
    store_format_id?: number;
    trade_type_id?: number;
    store_class_id?: number;

    store_code?: string;
    ref_id_1?: string;
    ref_id_2?: string;
    g_code?: string;
    address?: string;

    manager_name?: string;
    manager_contact?: string;
    manager_email?: string;
    opening_hours?: string;
    meta?: any;
    geo_fence?: number
    is_photo_allowed?: number;
}


export interface UpdateStore {
    en_name?: string;
    ar_name?: string;
    status?: number;

    country_id?: number;
    region_id?: number;
    province_id?: number;
    city_id?: number;
    district_id?: number;
    area_id?: number;
    channel_id?: number;
    sub_channel_id?: number;
    chain_id?: number;
    store_format_id?: number;
    trade_type_id?: number;
    store_class_id?: number;

    store_code?: string;
    ref_id_1?: string;
    ref_id_2?: string;
    g_code?: string;
    address?: string;

    manager_name?: string;
    manager_contact?: string;
    manager_email?: string;
    opening_hours?: string;
    meta?: any;
    geo_fence?: number;
    is_photo_allowed?: number;
}

export interface StoreCoverageIdle {
    total_stores: string;
    covered_stores: number;
    idle_stores: number;
}


class StoreRepository {

    async getAll(
        page: number = 1,
        limit: number = 10,
        filter?: Record<string, any>
    ): Promise<PaginatedResponse<Store>> {

        const params = new URLSearchParams({
            page: String(page),
            limit: String(limit)
        });

        if (filter) {
            for (const [key, value] of Object.entries(filter)) {
                if (value !== undefined && value !== null && value !== '') {
                    params.append(key, String(value as QueryValue));
                }
            }
        }

        return apiClient.get<PaginatedResponse<Store>>(
            `/stores?${params.toString()}`
        );
    }


    async getAllBasic(page: number = 1, limit: number = 10, filter?: any): Promise<PaginatedResponse<Store>> {

        const params = new URLSearchParams({
            page: page.toString(),
            limit: limit.toString()
        });

        if (filter) {
            for (const [key, value] of Object.entries(filter)) {
                if (value !== undefined && value !== null && value !== '') {
                    params.append(key, String(value as QueryValue));
                }
            }
        }

        return apiClient.get<PaginatedResponse<Store>>(`/stores/basic?${params.toString()}`)

    }


    async getById(id: number): Promise<Store> {
        return apiClient.get(`/stores/${id}`);

    }

    async create(data: CreateStore): Promise<Store> {
        return apiClient.post('/stores', data);

    }

    async update(id: number, data: UpdateStore): Promise<Store> {

        return apiClient.put(`/stores/${id}`, data);
    }

    async delete(id: number): Promise<{ message: string }> {
        return apiClient.delete<{ message: 'string' }>(`/stores/${id}`)

    }

    /**
    * Export channels to Excel
    */
    async exportToExcel(filters?: any): Promise<Blob> {
        const params = new URLSearchParams()
        if (filters) {
            for (const [key, value] of Object.entries(filters)) {
                if (value !== undefined && value !== null && value !== '') {
                    params.append(key, String(value as QueryValue));
                }
            }
        }
        const baseUrl = `/stores/export/excel`;
        const queryString = params.toString();
        const url = queryString ? `${baseUrl}?${queryString}` : baseUrl;

        return apiClient.getBlob(url, true, { showSuccessToast: true, successMessage: 'Exported Successfully' })
    }
    async exportTemplateToExcel(filters?: any): Promise<Blob> {
        const params = new URLSearchParams()
        if (filters?.status) params.append('status', filters.status)
        if (filters?.chain_id) params.append('chain_id', filters.chain_id.toString())
        if (filters?.channel_id) params.append('channel_id', filters.channel_id.toString())
        if (filters?.store_class_id) params.append('store_format_id', filters.store_format_id.toString())
        if (filters?.trade_type_id) params.append('trade_type_id', filters.trade_type_id.toString())
        if (filters?.store_class_id) params.append('store_class_id', filters.store_class_id.toString())

        const baseUrl = `/stores/export/template`;
        const queryString = params.toString();
        const url = queryString ? `${baseUrl}?${queryString}` : baseUrl;

        return apiClient.getBlob(url, true, { showSuccessToast: true, successMessage: 'Exported Successfully' });
    }
    // Analytics Methods
    async getActiveInactiveStores() {
        return apiClient.get<{ active_stores: number; inactive_stores: number; in_coverage: number }>(
            '/stores/analytics/active-inactive'
        )
    }

    async getCountryBreakdown() {
        return apiClient.get<Array<{ country: string; total_stores: number; percentage: number }>>(
            '/stores/analytics/countries', true, { silent: true }
        )
    }

    async getRegionBreakdown() {
        return apiClient.get<Array<{ country: string; region: string; total_stores: number; percentage: number }>>(
            '/stores/analytics/regions', true, { silent: true }
        )
    }

    async getProvinceBreakdown() {
        return apiClient.get<Array<{ country: string; province: string; total_stores: number; percentage: number }>>(
            '/stores/analytics/provinces', true, { silent: true }
        )
    }

    async getCityBreakdown() {
        return apiClient.get<Array<{ country: string; region: string; city: string; total_stores: number; percentage: number }>>(
            '/stores/analytics/cities', true, { silent: true }
        )
    }

    async getChannelBreakdown() {
        return apiClient.get<Array<{ channel: string; total_stores: number; percentage: number }>>(
            '/stores/analytics/channels', true, { silent: true }
        )
    }

    async getSubChannelBreakdown() {
        return apiClient.get<Array<{ sub_channel: string; total_stores: number; percentage: number }>>(
            '/stores/analytics/sub-channels', true, { silent: true }
        )
    }

    async getStoreFormatBreakdown() {
        return apiClient.get<Array<{ store_format: string; total_stores: number; percentage: number }>>(
            '/stores/analytics/store-formats'
        )
    }

    async getTradeTypeBreakdown() {
        return apiClient.get<Array<{ store_trade_type: string; total_stores: number; percentage: number }>>(
            '/stores/analytics/trade-types'
        )
    }

    async getStoreClassBreakdown() {
        return apiClient.get<Array<{ store_class: string; total_stores: number; percentage: number }>>(
            '/stores/analytics/store-classes'
        )
    }

    async getChainBreakdown() {
        return apiClient.get<Array<{
            chain: string;
            total_stores: number;
            percentage: number;
            num_regions: number;
            num_cities: number;
            num_channels: number;
            num_sub_channels: number;
            num_store_formats: number;
            num_trade_types: number;
            num_store_classes: number;
        }>>(
            '/stores/analytics/chains'
        )
    }

    async getCityDetailedBreakdown() {
        return apiClient.get<Array<{
            city: string;
            total_stores: number;
            percentage: number;
            num_chains: number;
            num_channels: number;
            num_sub_channels: number;
            num_store_formats: number;
            num_trade_types: number;
            num_store_classes: number;
        }>>(
            '/stores/analytics/cities-detailed'
        )
    }

    /**
     * Import stores from Excel file
     */
    async importFromExcel(file: File): Promise<{
        success: boolean;
        message: string;
        data: {
            success: boolean;
            total: number;
            created: number;
            updated: number;
            errors: Array<{ row: number; message: string }>;
        };
    }> {
        const formData = new FormData()
        formData.append('file', file)
        return apiClient.post('/stores/import/excel', formData)
    }

    async getTotalCoverageIdles(): Promise<StoreCoverageIdle[]> {
        return apiClient.get<StoreCoverageIdle[]>('/stores/analytics/coverage-idle');
    }
}

export const storeRepository = new StoreRepository;